import { Handle, Position } from '@xyflow/react';
import { Stack, Select, NumberInput, Switch } from '@mantine/core';
import { IconListNumbers } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { transferToQueueWidgetCore, type TransferToQueueConfig } from '@legacy-shared/widgets/core/transferToQueue';
import { graphql } from '../../lib/graphql';
import { useOrg } from '../../hooks/use-org';

export { type TransferToQueueConfig } from '@legacy-shared/widgets/core/transferToQueue';

const QueuesSubscription = graphql(`
  subscription WidgetQueues($organizationId: Uuid!) {
    queues(where: { organizationId: { _eq: $organizationId } }, orderBy: { createdAt: ASC }) {
      id
      name
    }
  }
`);

function TransferToQueueNode({ id, data, selected }: WidgetNodeProps<TransferToQueueConfig>) {
  return (
    <WidgetNodeShell
      icon={IconListNumbers}
      title={data.label || 'Transfer to Queue'}
      subtitle={data.config.queueName || 'No queue selected'}
      configured={!!data.config.queueId}
      errors={data.errors}
      selected={selected}
      color="teal"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="answered" label="Answered" offset="33%" nodeId={id} />
      <LabeledHandle type="source" position="bottom" id="timeout" label="Timeout" offset="67%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="overflow" label="Overflow" offset="33%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="abandoned" label="Abandoned" offset="67%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function TransferToQueueConfigPanel({ config, onChange }: ConfigPanelProps<TransferToQueueConfig>) {
  const { organizationId } = useOrg();

  const [{ data }] = useSubscription({
    query: QueuesSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const queues = data?.queues;

  const queueOptions = (queues ?? []).map((q) => ({
    value: q.id,
    label: q.name,
  }));

  return (
    <Stack gap="md">
      <Select
        label="Queue"
        description="Select the queue to route callers to"
        data={queueOptions}
        value={config.queueId || null}
        onChange={(v) => {
          const queue = queues?.find((q) => q.id === v);
          onChange({
            ...config,
            queueId: v ?? '',
            queueName: queue?.name ?? '',
          });
        }}
        searchable
      />
      <NumberInput
        label="Priority"
        description="Lower numbers = higher priority. Use negative values for VIP callers."
        value={config.priority}
        onChange={(v) => onChange({ ...config, priority: typeof v === 'number' ? v : 0 })}
        min={-10}
        max={10}
      />
      <Switch
        label="Announce Queue Position"
        description="Periodically tell callers their position in the queue"
        checked={config.announcePosition}
        onChange={(e) => onChange({ ...config, announcePosition: e.currentTarget.checked })}
      />
    </Stack>
  );
}

export const transferToQueueWidget: WidgetDefinition<TransferToQueueConfig> = {
  ...transferToQueueWidgetCore,
  icon: 'IconListNumbers',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'answered', type: 'success', label: 'Answered', position: 'bottom' },
      { id: 'timeout', type: 'timeout', label: 'Timeout', position: 'bottom' },
      { id: 'overflow', type: 'failure', label: 'Overflow', position: 'right' },
      { id: 'abandoned', type: 'failure', label: 'Abandoned', position: 'right' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: TransferToQueueNode,
    configPanel: TransferToQueueConfigPanel,
    preview: (c) => c.queueName || 'No queue selected',
  },

  validation: {
    ...transferToQueueWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
